import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Conversation, Message } from '@teentack/shared';
import { EmptyState } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useConversation,
  useConversations,
  useJoinConversation,
  useMarkConversationRead,
  useMessages,
  useSendMessage,
} from '@/features/messaging/use-messaging';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ChatIcon, EyeIcon, ShieldCheckIcon } from '@/components/ui/Icons';
import { ErrorNote } from '@/components/Form';
import { relativeTime } from '@/lib/relative-time';
import { presentApiError } from '@/lib/form-errors';

/**
 * Messages. [Teen steps 11-12, Parent steps 8/11/13, Job Poster steps 15/21]
 *
 * Two panes on a desktop, one at a time on a phone. The thing this screen has
 * to be honest about is who else is reading: a minor's thread always has their
 * guardian in it, and that is stated at the top rather than buried in a
 * participant list.
 */

/** Everyone in the thread except the person looking at it. */
function otherNames(conversation: Conversation): string {
  const others = conversation.participants.filter((participant) => !participant.isMe);
  const speakers = others.filter((participant) => participant.visibility === 'FULL');
  return (speakers.length > 0 ? speakers : others).map((p) => p.name).join(', ');
}

function watcherNames(conversation: Conversation): string[] {
  return conversation.participants
    .filter((participant) => participant.visibility === 'MONITOR_ONLY')
    .map((participant) => participant.name);
}

function ThreadListItem({
  conversation,
  active,
}: {
  conversation: Conversation;
  active: boolean;
}) {
  return (
    <li>
      <Link
        to={`/messages/${conversation.id}`}
        className={`block border-l-2 px-4 py-3.5 transition ${
          active
            ? 'border-brand-600 bg-brand-50/70'
            : 'border-transparent hover:border-brand-200 hover:bg-slate-50'
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <p className="truncate text-sm font-bold text-ink">{otherNames(conversation)}</p>
          {conversation.unreadCount > 0 && (
            <span className="inline-flex min-w-5 shrink-0 items-center justify-center rounded-full bg-brand-600 px-1.5 py-0.5 text-[0.65rem] font-bold text-white">
              {conversation.unreadCount}
            </span>
          )}
        </div>
        {conversation.job && (
          <p className="mt-0.5 truncate text-xs font-medium text-slate-500">
            {conversation.job.title}
          </p>
        )}
        {conversation.lastMessage && (
          <p className="mt-1 truncate text-xs text-slate-400">
            {conversation.lastMessage.isMine ? 'You: ' : ''}
            {conversation.lastMessage.content}
          </p>
        )}
      </Link>
    </li>
  );
}

function Bubble({ message }: { message: Message }) {
  if (message.isMine) {
    return (
      <li className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-brand-600 px-4 py-2.5 text-white">
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
          <p className="mt-1 text-right text-[0.7rem] text-white/70">
            {relativeTime(message.sentAt)}
          </p>
        </div>
      </li>
    );
  }

  return (
    <li className="flex justify-start">
      <div className="max-w-[85%] rounded-2xl rounded-bl-md border border-line bg-white px-4 py-2.5">
        <p className="text-[0.7rem] font-bold uppercase tracking-wide text-slate-400">
          {message.senderName}
        </p>
        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-ink">
          {message.content}
        </p>
        <p className="mt-1 text-[0.7rem] text-slate-400">{relativeTime(message.sentAt)}</p>
      </div>
    </li>
  );
}

function Thread({ conversationId }: { conversationId: string }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const conversation = useConversation(conversationId);
  const messages = useMessages(conversationId);
  const send = useSendMessage(conversationId);
  const markRead = useMarkConversationRead();
  const join = useJoinConversation();

  // Opening a thread is reading it. Keyed on the id so switching threads marks
  // the new one rather than repeatedly marking the old.
  useEffect(() => {
    markRead.mutate(conversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // The api sends newest first; the reader wants oldest first.
  const ordered = [...(messages.data?.items ?? [])].reverse();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [ordered.length]);

  async function onSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const content = draft.trim();
    if (!content) return;

    setError(null);
    try {
      await send.mutateAsync({ content });
      setDraft('');
    } catch (caught) {
      setError(presentApiError(caught, 'Could not send that').message);
    }
  }

  const data = conversation.data;
  const watchers = data ? watcherNames(data) : [];
  const iAmWatching = data ? !data.canSend : false;

  return (
    <div className="flex h-[calc(100vh-13rem)] flex-col rounded-2xl border border-line bg-canvas shadow-soft">
      {/* Who is in the room */}
      <header className="shrink-0 border-b border-line bg-white px-5 py-3.5">
        {conversation.isPending && <Skeleton className="h-10" radius="md" />}
        {data && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <h2 className="truncate text-sm font-bold text-ink">{otherNames(data)}</h2>
                {data.job && (
                  <Link
                    to={`/jobs/${data.job.id}`}
                    className="text-xs font-medium text-brand-600 hover:underline"
                  >
                    {data.job.title}
                  </Link>
                )}
              </div>
              {data.type === 'THREE_WAY' && <Badge tone="accent">Three-way</Badge>}
            </div>

            {watchers.length > 0 && (
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-sun-50 px-2.5 py-1 text-xs font-medium text-sun-700">
                <EyeIcon className="size-3.5" />
                {watchers.join(', ')} can read this conversation
              </p>
            )}
          </>
        )}
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {messages.isPending && (
          <div className="space-y-3" aria-busy="true">
            <Skeleton className="h-12 w-2/3" />
            <Skeleton className="ml-auto h-12 w-1/2" />
          </div>
        )}

        {messages.data && ordered.length === 0 && (
          <p className="py-10 text-center text-sm text-slate-500">
            No messages yet. Say hello.
          </p>
        )}

        <ul className="space-y-3">
          {ordered.map((message) => (
            <Bubble key={message.id} message={message} />
          ))}
        </ul>
        <div ref={bottomRef} />
      </div>

      {/* Composer, or the reason there is not one */}
      <footer className="shrink-0 border-t border-line bg-white px-5 py-3.5">
        {error && (
          <div className="mb-2">
            <ErrorNote message={error} />
          </div>
        )}

        {iAmWatching ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="inline-flex items-center gap-2 text-sm text-slate-500">
              <ShieldCheckIcon className="size-4 text-slate-400" />
              You are watching this conversation.
            </p>
            <Button
              variant="outline"
              disabled={join.isPending}
              onClick={() => join.mutate(conversationId)}
            >
              {join.isPending ? 'Joining…' : 'Join the conversation'}
            </Button>
          </div>
        ) : (
          <form onSubmit={(event) => void onSubmit(event)} className="flex items-end gap-2">
            <label htmlFor="message-draft" className="sr-only">
              Message
            </label>
            <textarea
              id="message-draft"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                // Enter sends, Shift+Enter makes a new line — what people expect.
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void onSubmit(event);
                }
              }}
              rows={2}
              maxLength={4000}
              placeholder="Write a message…"
              className="min-h-11 flex-1 resize-none rounded-xl border border-line bg-white px-3.5 py-2.5 text-sm text-ink outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
            />
            <Button type="submit" disabled={send.isPending || draft.trim().length === 0}>
              {send.isPending ? 'Sending…' : 'Send'}
            </Button>
          </form>
        )}
      </footer>
    </div>
  );
}

export function MessagesPage() {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const conversations = useConversations({ pageSize: 50 });

  const threads = conversations.data?.items ?? [];

  return (
    <main className="container-page py-10">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
          Your account
        </p>
        <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
          Messages
        </h1>
        <p className="mt-2 max-w-prose text-sm leading-relaxed text-slate-500">
          Conversations about jobs you are involved in. A guardian can always read a minor’s
          thread.
        </p>
      </header>

      {conversations.isError && (
        <ErrorNote
          message={presentApiError(conversations.error, 'Could not load your messages').message}
        />
      )}

      {conversations.isPending && (
        <div className="space-y-3" aria-busy="true">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
      )}

      {conversations.data && threads.length === 0 && (
        <EmptyState icon={<ChatIcon className="size-6" />} title="No conversations yet">
          Messages start from a job. Apply for something, or open a job you have posted and
          message an applicant.
        </EmptyState>
      )}

      {threads.length > 0 && (
        <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
          {/* The list hides on a phone once a thread is open. */}
          <aside className={conversationId ? 'hidden lg:block' : ''}>
            <div className="overflow-hidden rounded-2xl border border-line bg-white shadow-soft">
              <ul className="max-h-[calc(100vh-13rem)] divide-y divide-line overflow-y-auto">
                {threads.map((conversation) => (
                  <ThreadListItem
                    key={conversation.id}
                    conversation={conversation}
                    active={conversation.id === conversationId}
                  />
                ))}
              </ul>
            </div>
          </aside>

          <section className={conversationId ? '' : 'hidden lg:block'}>
            {conversationId ? (
              <>
                <Link
                  to="/messages"
                  className="mb-3 inline-block text-sm font-semibold text-brand-600 hover:underline lg:hidden"
                >
                  ← All conversations
                </Link>
                <Thread conversationId={conversationId} />
              </>
            ) : (
              <div className="grid h-full place-items-center rounded-2xl border border-dashed border-line bg-white p-12 text-center">
                <p className="text-sm text-slate-500">Pick a conversation to read it.</p>
              </div>
            )}
          </section>
        </div>
      )}
    </main>
  );
}
